import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, CheckCircle, XCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import api from '../lib/api';
import useSEO from '../hooks/useSEO';

export default function ResetPassword() {
  useSEO({ title: 'Reset password — EpisodeKit', path: '/reset-password', index: false });
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState(token ? 'form' : 'no-token'); // form | loading | success | error | no-token
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setMessage('Password must be at least 6 characters.');
      setStatus('error');
      return;
    }
    if (password !== confirm) {
      setMessage('Passwords do not match.');
      setStatus('error');
      return;
    }
    setStatus('loading');
    try {
      const res = await api.post('/auth/reset-password', { token, new_password: password });
      setStatus('success');
      setMessage(res.data.message);
    } catch (err) {
      setStatus('error');
      setMessage(err.response?.data?.detail || 'Failed to reset password. The link may be expired.');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-950 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-neutral-900 border border-neutral-800 rounded-xl p-8" data-testid="reset-password-page">
        {status === 'form' && (
          <>
            <Lock className="w-10 h-10 text-violet-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white text-center mb-2">Set New Password</h1>
            <p className="text-neutral-400 text-sm text-center mb-6">Enter your new password below.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="New password (min 6 chars)"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                data-testid="new-password-input"
                required
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="bg-neutral-800 border-neutral-700 text-white"
                data-testid="confirm-password-input"
                required
              />
              <Button type="submit" className="bg-violet-600 hover:bg-violet-700 w-full" data-testid="reset-submit">
                Reset Password
              </Button>
            </form>
          </>
        )}
        {status === 'loading' && (
          <div className="text-center">
            <Loader2 className="w-10 h-10 text-violet-500 animate-spin mx-auto mb-4" />
            <p className="text-neutral-400">Resetting password...</p>
          </div>
        )}
        {status === 'success' && (
          <div className="text-center">
            <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Password Reset</h1>
            <p className="text-neutral-400 text-sm mb-6">{message}</p>
            <Link to="/login">
              <Button className="bg-violet-600 hover:bg-violet-700 w-full" data-testid="go-to-login">Go to Login</Button>
            </Link>
          </div>
        )}
        {status === 'error' && (
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Error</h1>
            <p className="text-neutral-400 text-sm mb-6">{message}</p>
            <Button variant="outline" className="border-neutral-700 text-neutral-300" onClick={() => setStatus('form')} data-testid="try-again">
              Try Again
            </Button>
          </div>
        )}
        {status === 'no-token' && (
          <div className="text-center">
            <XCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Invalid Link</h1>
            <p className="text-neutral-400 text-sm mb-6">This reset link is missing or invalid. Please request a new one.</p>
            <Link to="/forgot-password">
              <Button variant="outline" className="border-neutral-700 text-neutral-300 w-full">Request New Link</Button>
            </Link>
          </div>
        )}
        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
}
